import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

// Locally-persisted preferences. Values must be JSON-serializable.

export type PrefKey =
  | 'flagOverrides'
  | 'uploadLanguageCode'
  | 'exportTranscriptFormat'
  | 'exportTranscriptIncludeTimestamps'
  | 'exportTranscriptIncludeSpeakers'
  | 'exportTranscriptSpeakersInSeparateColumn'
  | 'captionLineLength'
  | 'reviewCorrectionHelpHidden'
  | 'youTubeImportHelpHidden'
  | 'referralBadgeClicked'
  | 'detailsPanelDirection'
  // Navigation preferences
  | 'nav.sidebarCollapsed'
  | 'nav.group.filesExpanded'
  | 'nav.group.reviewQueueExpanded'
  | 'nav.group.settingsExpanded'
  | 'nav.group.organizationSettingsExpanded'
  | 'nav.group.liveStreamsExpanded'
  // Video player preferences
  | 'player.captionOptions'
  | 'player.audioDescriptionOptions'
  | 'player.audioDescriptionMode'
  | 'player.highContrast'
  | 'player.playbackRate'
  | 'player.volume'
  | 'player.captionsEnabled'
  // Any table ID like 'table.files.sortColumn'
  | `table.${string}.sortColumn`
  | `table.${string}.sortAscending`
  | 'segmentExportFormat'
  | 'segmentExportIncludeTimestamps'
  // Live stream preferences
  | 'liveStreamShortcuts'
  // Live caption voiceover preferences
  | 'liveCaptions.voiceoverEnabled'
  | 'liveCaptions.voiceoverVoiceName'
  | 'liveCaptions.voiceoverVolume'
  | 'liveCaptions.voiceoverRate'
  | 'liveCaptions.voiceoverMode';

const prefix = 'recap.preference.';

export function deletePref(key: PrefKey): void {
  try {
    localStorage.removeItem(prefix + key);
  } catch (error) {}
}

export function setPref<T>(key: PrefKey, value: T): void {
  try {
    localStorage.setItem(prefix + key, JSON.stringify(value));
  } catch (error) {}
}

export function getPref<T>(key: PrefKey, defaultValue: T): T {
  try {
    const value = localStorage.getItem(prefix + key);
    return value !== null ? JSON.parse(value) : defaultValue;
  } catch (error) {
    return defaultValue;
  }
}

/** Query key for a preference. Use with invalidateQueries when updating outside usePref. */
export function prefQueryKey(key: PrefKey) {
  return ['pref', key] as const;
}

export function usePref<T>(
  key: PrefKey,
  defaultValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const queryClient = useQueryClient();

  const defaultValueRef = useRef(defaultValue);
  defaultValueRef.current = defaultValue;

  const { data: value = defaultValue } = useQuery({
    queryKey: prefQueryKey(key),
    queryFn: () => Promise.resolve(getPref(key, defaultValue)),
    staleTime: Infinity,
  });

  const setValue = useCallback(
    (valueOrUpdater: T | ((prev: T) => T)) => {
      const current = queryClient.getQueryData<T>(prefQueryKey(key)) ?? defaultValueRef.current;
      const newValue =
        typeof valueOrUpdater === 'function'
          ? (valueOrUpdater as (prev: T) => T)(current)
          : valueOrUpdater;
      setPref(key, newValue);
      queryClient.setQueryData(prefQueryKey(key), newValue);
    },
    [key, queryClient],
  );

  return [value, setValue];
}
